/**
 * Hero Game Slide Component
 * Individual slide in the hero carousel
 */
import { motion } from 'framer-motion';
import { memo } from 'react';
import BoosterPack from '../BoosterPack';
import GameCTAButton from '../GameCTAButton';

const PACK_POSITIONS = [
  'hidden -translate-x-20 -rotate-18 opacity-0 [@media(min-width:1415px)]:group-hover:-translate-x-95 [@media(min-width:1415px)]:group-hover:-rotate-20 [@media(min-width:1415px)]:group-hover:opacity-100 scale-85 z-10',
  '-translate-x-15 -rotate-5 opacity-100 [@media(min-width:1415px)]:group-hover:-translate-x-60 [@media(min-width:1415px)]:group-hover:-rotate-10 scale-95 z-20',
  '-translate-x-5 rotate-2 opacity-100 [@media(min-width:1415px)]:group-hover:-translate-x-30 [@media(min-width:1415px)]:group-hover:-rotate-2 z-30',
  '-translate-x-10 rotate-14 opacity-0 [@media(min-width:1415px)]:group-hover:translate-x-0 [@media(min-width:1415px)]:group-hover:rotate-12 [@media(min-width:1415px)]:group-hover:opacity-100 scale-90 z-20',
];

const GameSlide = memo(
  ({
    game,
    idx,
    shineDisabled,
    setShineDisabled,
    onNavigateToStock,
    onPackClick,
  }) => {
    const {
      image,
      logo: gameLogo,
      packs,
      name,
      bgSpeedLinesHueRot,
      bgCol,
    } = game;

    return (
      <motion.section
        key={idx}
        data-idx={idx}
        className="bg-after relative z-5 flex h-full w-screen flex-shrink-0 snap-center items-center justify-center overflow-hidden border-b-1 border-b-orange-400/70 text-center after:h-full after:max-h-full after:!w-[100vw] after:!max-w-[100vw] after:bg-[url(./speed-lines.webp)] after:bg-cover md:after:bg-cover"
        style={{
          '--hue': bgSpeedLinesHueRot,
          backgroundColor: bgCol || '#000000',
          backgroundImage: `url(${image})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'auto 100%',
          scrollSnapStop: 'always',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative z-10 h-full w-full">
          <div className="mx-auto flex h-full w-full max-w-[96vw] flex-col items-center justify-center px-4 md:max-w-[90vw]">
            <div
              id="logo-container"
              className="flex w-full items-center justify-between"
            >
              <div className="flex h-full max-w-[380px] flex-col items-center justify-between">
                <div className="mt-25 flex flex-grow flex-col items-center justify-center gap-4">
                  <img
                    src={gameLogo}
                    alt={`${name} logo`}
                    className="max-h-[380px] max-w-[350px] object-contain py-3"
                  />
                  <div className="mt-6">
                    <GameCTAButton game={game} onSelect={onNavigateToStock} />
                  </div>
                </div>
              </div>

              {/* Booster pack fan */}
              <motion.div>
                <div
                  id="cards"
                  className="group relative -my-4 mt-48 h-100 max-h-100 w-60 max-w-60"
                >
                  {packs.map(([src, packName], i) => {
                    const baseClasses =
                      'absolute top-0 left-0 h-80 max-h-80 w-47 max-w-47 transform transition-all duration-300 ease-in-out';
                    const positionClasses = PACK_POSITIONS[i] || '';

                    return (
                      <div
                        key={i}
                        className="cursor-pointer"
                        onClick={() => onPackClick(packName)}
                      >
                        <BoosterPack
                          src={src}
                          alt=""
                          className={`${baseClasses} ${positionClasses}`}
                          shineDisabled={shineDisabled}
                          disableShineGlobally={() => setShineDisabled(true)}
                        />
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>
    );
  },
);

GameSlide.displayName = 'GameSlide';

export default GameSlide;
